/**
 * Encapsulates opening the estimate-value radio modal, persisting the selection
 * to `estimate_value`, and triggering a post-success table refetch.
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  RadioSelectModalMetaData,
  RadioSelectOption,
} from "../../../../components/modals/radioSelectModal/radioSelectModal";
import { ToastMessages } from "../../../../components/toasts/toastMessages";
import { BOOptionModel } from "../../../../data-model/boOptionModel";
import { ModalId } from "../../../../enums/modalId";
import { SingleOrderDetailsTableName } from "../../../../enums/singleOrderDetailsTableName";
import { useUpdateOrderDetails } from "../../../../hooks/singleMedicalPaOrderDetails/useUpdateOrderDetails";
import {
  closeModal,
  setOpenedModalId,
} from "../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import {
  BoStatusModalTableRow,
  buildBoStatusModalMetaData,
} from "../utils/buildBoStatusModalMetaData";

/** Props for {@link useBoStatusRadioSelect}. */
interface UseBoStatusRadioSelectProps {
  /** Current worklist rows (for preselected estimate value). */
  tableData: BoStatusModalTableRow[];
  /** Estimate options from Redux / org `estimate_options` config. */
  estimateOptions: BOOptionModel[] | undefined;
  /** Invoked after a successful status update (e.g. `/search` refetch). */
  onUpdated: () => void;
}

/**
 * Manages estimate-value radio modal open + save: tracks the active order id for
 * `useUpdateOrderDetails`, dispatches modal open metadata, loading state, and
 * closes the modal after update with success/error toasts.
 *
 * @param props.tableData - Table rows for building modal metadata
 * @param props.estimateOptions - Estimate dropdown options
 * @param props.onUpdated - Callback after successful save
 * @returns `openBoStatusModal` and `saveBoStatus` handlers for the table and modal
 */
export const useBoStatusRadioSelect = ({
  tableData,
  estimateOptions,
  onUpdated,
}: UseBoStatusRadioSelectProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [activeOrderId, setActiveOrderId] = useState("");

  const modalMetaData = useSelector(
    (state: RootState) => state.modal.metaData,
  ) as RadioSelectModalMetaData | undefined;

  const orgId = getOrgIdForFetchExternalWorklist();

  const { runUpdate: runUpdateOrderStatus } = useUpdateOrderDetails({
    orderId: activeOrderId,
    orgId,
    tableName: SingleOrderDetailsTableName.ORDER_STATUS,
  });

  /**
   * Sets the active order and opens the radio modal with pre-filled metadata.
   *
   * @param orderId - PA order id to edit
   */
  const openBoStatusModal = useCallback(
    (orderId: string) => {
      setActiveOrderId(orderId);
      dispatch(
        setOpenedModalId({
          id: ModalId.RADIO_SELECT_MODAL,
          metaData: buildBoStatusModalMetaData(
            orderId,
            tableData,
            estimateOptions,
          ),
        }),
      );
    },
    [estimateOptions, dispatch, tableData],
  );

  /**
   * Persists the selected value to the order's `estimate_value` field via the
   * order-status update, refetches on success, and closes the modal.
   *
   * @param selected - Chosen radio option `{ label, value }`
   * @param orderId - Order id from modal metadata (must match active selection)
   */
  const saveBoStatus = useCallback(
    async (selected: RadioSelectOption, orderId: string) => {
      dispatch(
        setOpenedModalId({
          id: ModalId.RADIO_SELECT_MODAL,
          metaData: {
            ...(modalMetaData ?? {}),
            order_id: orderId,
            isLoading: true,
          },
        }),
      );

      const result = await runUpdateOrderStatus({
        estimate_value: selected.value,
      });

      if (result.success) {
        toast.success(ToastMessages.ORDER_STATUS_UPDATED_SUCCESS);
        onUpdated();
        dispatch(closeModal());
      } else {
        toast.error(ToastMessages.ORDER_STATUS_UPDATED_FAILED);
        dispatch(closeModal());
      }
    },
    [dispatch, modalMetaData, onUpdated, runUpdateOrderStatus],
  );

  return { openBoStatusModal, saveBoStatus };
};
